import React, { useState } from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { UpdatePhoto } from 'components/pages/profile/UpdatePhoto';
import { useDispatch } from 'store/hooks';
import { updateProfile } from 'store/auth/actions';
import { useSnackbar } from 'components/Snackbar';
import { Avatar as ShadcnAvatar } from '@repo/shadcn-ui';
import { PencilIcon, QrCodeIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import { pushState } from 'hooks/usePushPage';

interface ProfileAvatarProps {
  avatarImage: string;
  firstName: string;
  lastName: string;
  onFileSelect: (file: File) => void;
  onAvatarRemoved: () => void;
}

/** Edge of the round avatar. */
const AVATAR_SIZE = 80;
/** Edge of the edit badge that overlaps its bottom-right corner. */
const BADGE_SIZE = 32;
/** Edge of the QR button beside it. */
const CODE_BUTTON_SIZE = 48;

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  avatarImage,
  firstName,
  lastName,
  onFileSelect,
  onAvatarRemoved,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isUpdatePhotoOpen, setIsUpdatePhotoOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();

  const handleRemoveAvatar = () => {
    dispatch(
      updateProfile({ avatar: null } as any, ({ success }) => {
        if (success) {
          onAvatarRemoved();
          openSnackbar({ severity: 'success', title: 'Photo removed' });
        }
      })
    );
  };

  return (
    <>
      {/*
        VERA-304 — the `Change` button is gone and its job moved ONTO the image,
        as an edit badge.

        A separate button was there because hover does not exist on the phone
        this app is built for, so a bare avatar signalled nothing. A badge solves
        that without the button: it is a permanent, visible mark ON the thing it
        edits, which is the pattern every phone app the user already has uses for
        a profile photo. It also frees the row beside the avatar, which is where
        the code now lives.

        The badge is only 32px, below the 44px touch guidance — but the ENTIRE
        80px avatar fires the same action, so the real target is 80px with a
        32px label on it. The badge is the signal; the avatar is the target.
      */}
      <Stack direction='row' alignItems='center' spacing={2}>
        <Box sx={{ position: 'relative', width: AVATAR_SIZE, height: AVATAR_SIZE }}>
          <Box
            role='button'
            tabIndex={0}
            aria-label='Change profile photo'
            onClick={() => setIsUpdatePhotoOpen(true)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setIsUpdatePhotoOpen(true);
              }
            }}
            sx={{
              cursor: 'pointer',
              borderRadius: '50%',
              '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 3px ${theme.palette.action.focus}`,
              },
            }}
          >
            <ShadcnAvatar
              src={avatarImage || undefined}
              size='2xl'
              noImageIcon='profile'
              initials={`${firstName.slice(0, 1)}${lastName.slice(0, 1)}`}
            />
          </Box>

          {/*
            A pencil, not the camera glyph that was here before. The camera named
            ONE of the two ways to supply a photo — the UpdatePhoto sheet offers
            take-a-photo, choose-from-library and remove — so it under-described
            the control it labelled. The pencil names the outcome instead.

            `pointerEvents: none` is deliberate: the badge is inside the avatar's
            own hit area, so letting it capture the click would only create a
            second handler for the identical action. It is a label on a button,
            not a button.
          */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              bottom: -4,
              right: -4,
              width: BADGE_SIZE,
              height: BADGE_SIZE,
              borderRadius: `${theme.shape.borderRadius}px`,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <PencilIcon size={16} strokeWidth={1.5} />
          </Box>
        </Box>

        {/*
          The code lives BESIDE the avatar, not on it.

          Venmo puts its QR badge on the avatar's corner, but Venmo has no photo
          editing on that screen — the corner is free. Here it is not: the edit
          badge holds it, and a second badge on an 80px circle would both crowd
          it and make the avatar's own tap ambiguous (it opens the photo flow,
          which the user could no longer predict from two competing marks).

          So the QR takes the slot the `Change` button vacated, which is also
          where the user asked for it. Icon-only rather than labelled: a QR glyph
          in this exact position is the same control in Venmo, WhatsApp,
          Instagram and PayPal, so the glyph is doing better than a label would
          at telling a phone user what happens next.
        */}
        <Box
          role='button'
          tabIndex={0}
          aria-label='Show my code'
          onClick={() => navigate(ROUTES.PROFILE_MY_CODE, { state: pushState() })}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate(ROUTES.PROFILE_MY_CODE, { state: pushState() });
            }
          }}
          sx={{
            width: CODE_BUTTON_SIZE,
            height: CODE_BUTTON_SIZE,
            flexShrink: 0,
            borderRadius: `${theme.shape.borderRadius}px`,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 150ms ease',
            '&:hover': { backgroundColor: theme.palette.grey[50] },
            '&:focus-visible': {
              outline: 'none',
              boxShadow: `0 0 0 3px ${theme.palette.action.focus}`,
            },
          }}
        >
          <QrCodeIcon size={24} strokeWidth={1.5} />
        </Box>
      </Stack>

      {isUpdatePhotoOpen && (
        <DrawerPopup
          title='Profile photo'
          titleUppercase={false}
          backgroundColor={theme.palette.background.paper}
          fullScreen={false}
          child={
            <UpdatePhoto
              onFileSelect={onFileSelect}
              onRemove={handleRemoveAvatar}
              handleClose={() => setIsUpdatePhotoOpen(false)}
            />
          }
          handleOpen={isUpdatePhotoOpen}
          handleClose={() => setIsUpdatePhotoOpen(false)}
        />
      )}
    </>
  );
};
